import { noop } from 'lodash-es';
import { serializeError } from 'serialize-error';

import type { FormattedTransaction, TransactionReceipt } from 'viem';
import type { PublicClient } from 'wagmi';
import { assertIsHex } from '@soundxyz/sdk/utils/helpers';
import { retryAsync } from '@soundxyz/utils';
import type { ChainProviderContainerValue } from '@context/chainProvider';
import { VerifyTransactionError } from '@utils/error';

import type {
  ExtendedContractMethod,
  IndividualTransaction,
  TransactionStatusLineItem,
  TransactionStatusParent,
  TransactionStatusStoreData,
  TransactionStatusStoreDataSchema,
} from './schema';
import { IndividualTransactionStatus, ParentTransactionStatus } from './schema';

const TRANSACTION_EXPIRATION_THRESHOLD_MS = 1000 * 60 * 5; // 5 minutes
const TRANSACTION_VERIFICATION_ATTEMPTS_THRESHOLD = 5; // won't try to verify more than 5 times

/**
 * Notes from @saihaj
 *
 * After spending many hours of debugging, I found that Optimism Goerli was
 * being very slow at returning the transaction receipt for the transaction
 * hash. This was causing transaction to show failed state even though, we
 * have no clue about the transaction status.
 *
 * So the solution is to retry getting the transaction receipt for a few times
 * before giving up. This is a temporary solution until we figure out why
 * Optimism Goerli is being slow.
 *
 * Even discussed this with the Alchemy team and basically if we are getting `null` as a response
 * from `provider.getTransaction` is not yet broadcasted to their nodes.
 *
 * More context:
 * - https://soundxyz.slack.com/archives/C03MAV9GAAK/p1685069194358029?thread_ts=1685056675.871599&cid=C03MAV9GAAK
 * - https://soundxyz.slack.com/archives/C0576PETC8Y/p1685107145387839
 *
 * Instead of polling an alternative solution is to use
 * WebSockets https://linear.app/sound/issue/ENG-6203/using-ws-api-to-listen-for-transaction-updates
 *
 */

export async function getTransactionResponse({
  hash,
  provider,
  chainId,
}: {
  hash: string;
  provider: PublicClient;
  chainId: number;
}) {
  assertIsHex(hash);

  return retryAsync<FormattedTransaction>(
    async () => {
      /**
       * Alchemy team suggested to use getTransactionReceipt instead of getTransaction because it has the `status` field.
       * https://linear.app/sound/issue/ENG-6204/swap-gettransaction-to-getreceipt
       *
       * @saihaj recommendation is it makes no difference because in both are going to return `null`
       *  if the transaction is not yet broadcasted to their nodes, so we are retry anyways.
       */
      const txResponse: FormattedTransaction = await provider.getTransaction({
        hash,
      });
      if (!txResponse) {
        throw new Error('Transaction response is undefined');
      }

      return txResponse;
    },
    {
      // we are basically trying up to 5 seconds, but this ensures
      // for any day to day scenario the transaction is for sure
      // is broadcasted to the nodes.
      /**
       * Block congestion/higher gas prices can cause additional delays
       * so it is better to be safe and retry aggressively.
       */
      attempts: 10,
      /**
       * On Optimism Goerli, it was taking around 800ms-1000ms to get the transaction
       * once it is mined. So it is a safe bet to retry for 500ms, so we are not sending
       * too many requests to the provider
       */
      interval: 500,
      logger: {
        error: noop,
        debug: noop,
        info: noop,
        warn: noop,
        child: function () {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const parentLogger = this;

          return {
            error: noop,
            debug: noop,
            info: noop,
            warn: noop,
            child: () => parentLogger,
          };
        },
      },
      operation: 'Retry transaction hash lookup',
      operationExtra: {
        chainId,
        hash,
      },
    },
  );
}

/**
 * Notes from @caieu
 * Same issue as above, but for transaction receipts.
 */
export async function getTransactionReceipt({
  hash,
  provider,
  chainId,
  interval,
}: {
  hash: string;
  provider: PublicClient;
  chainId: number;
  interval?: number;
}) {
  assertIsHex(hash);

  return retryAsync<TransactionReceipt>(
    async () => {
      return await provider.waitForTransactionReceipt({
        hash,
      });
    },
    {
      attempts: 10,
      interval: interval ?? 500,
      logger: {
        error: noop,
        debug: noop,
        info: noop,
        warn: noop,
        child: function () {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const parentLogger = this;

          return {
            error: noop,
            debug: noop,
            info: noop,
            warn: noop,
            child: () => parentLogger,
          };
        },
      },
      operation: 'Retry transaction receipt lookup',
      operationExtra: {
        chainId,
        hash,
      },
    },
  );
}

type OnChangeFn = (data: TransactionStatusStoreData) => void;

type TransactionProcessingHandler = (transaction: IndividualTransaction) => void | Promise<void>;

type TransactionCompleteHandler = (params: {
  lineItem: TransactionStatusLineItem;
  txResponse: FormattedTransaction | null;
}) => void | Promise<void>;

export class TransactionStatusStore {
  private data: TransactionStatusStoreData = {};

  // Fired whenever the internal state of the store changes.
  private readonly onChange: OnChangeFn;

  // Fired when we begin tracking the status of transaction,
  // either when it is initiated or after a page reload.
  private onTransactionProcessing: TransactionProcessingHandler;

  // Fired when we complete processing a transaction.
  private onTransactionComplete: TransactionCompleteHandler;

  constructor({
    onChange,
    onTransactionProcessing,
    onTransactionComplete,
  }: {
    onChange: OnChangeFn;
    onTransactionProcessing?: TransactionProcessingHandler;
    onTransactionComplete?: TransactionCompleteHandler;
  }) {
    this.onChange = onChange;
    this.onTransactionProcessing =
      onTransactionProcessing ||
      (() => {
        return;
      });
    this.onTransactionComplete =
      onTransactionComplete ||
      (() => {
        return;
      });
  }

  public setOnTransactionProcessing(onTransactionProcessing: TransactionProcessingHandler) {
    this.onTransactionProcessing = onTransactionProcessing;
  }

  public setOnTransactionComplete(onTransactionComplete: TransactionCompleteHandler) {
    this.onTransactionComplete = onTransactionComplete;
  }

  public reset() {
    this.data = {};
    this.onChange(this.data);
  }

  public hydrate(data: typeof TransactionStatusStoreDataSchema._output) {
    this.data = data;
  }

  private getTransactionStatus(receipt: TransactionReceipt): IndividualTransactionStatus {
    return receipt.status === 'success'
      ? IndividualTransactionStatus.SUCCESS
      : IndividualTransactionStatus.FAILED;
  }

  // Given a transaction, return the initial status line item
  private createTransactionStatusLineItem(
    transaction: IndividualTransaction,
  ): TransactionStatusLineItem {
    return {
      transaction,
      status: IndividualTransactionStatus.UNKNOWN,
      createdAt: new Date(),
      lastVerificationAttemptDispatchedAt: new Date(),
      lastVerificationAttemptCompletedAt: null,
      numVerificationAttempts: 1,
    };
  }

  private getTransactionKey(transaction: IndividualTransaction): string {
    return `${transaction.from.toLowerCase()}-${transaction.nonce}`;
  }

  private registerTransaction(transaction: IndividualTransaction): TransactionStatusLineItem {
    const key = this.getTransactionKey(transaction);
    const lineItem = this.createTransactionStatusLineItem(transaction);

    const existingParent = this.data[key];
    if (existingParent) {
      existingParent.items.unshift(lineItem);
    } else {
      const newParent: TransactionStatusParent = {
        items: [lineItem],
      };
      this.data[key] = newParent;
    }

    this.onChange(this.data);
    return lineItem;
  }

  private findTransactionStatusLineItem(
    input: IndividualTransaction,
  ): TransactionStatusLineItem | null {
    const key = this.getTransactionKey(input);
    const parent = this.data[key];
    if (!parent) {
      return null;
    }
    const transaction = parent.items.find(trx => {
      return trx.transaction.hash.toLowerCase() === input.hash.toLowerCase();
    });
    if (!transaction) {
      return null;
    }
    return transaction;
  }

  private registerTransactionStatusVerificationAttempt(transaction: IndividualTransaction) {
    const lineItem = this.findTransactionStatusLineItem(transaction);
    if (!lineItem) {
      return;
    }
    lineItem.lastVerificationAttemptDispatchedAt = new Date();
    lineItem.numVerificationAttempts += 1;
  }

  private registerTransactionStatusVerificationResult({
    transaction,
    status,
  }: {
    transaction: IndividualTransaction;
    status: IndividualTransactionStatus;
  }) {
    const lineItem = this.findTransactionStatusLineItem(transaction);
    if (!lineItem) {
      return;
    }
    lineItem.lastVerificationAttemptCompletedAt = new Date();
    lineItem.status = status;
  }

  private expireOldTransactions(now: Date) {
    Object.values(this.data).forEach(parent => {
      parent.items.forEach(lineItem => {
        const timeSinceFirstVerificationAttempt = now.getTime() - lineItem.createdAt.getTime();
        if (
          timeSinceFirstVerificationAttempt > TRANSACTION_EXPIRATION_THRESHOLD_MS ||
          lineItem.numVerificationAttempts > TRANSACTION_VERIFICATION_ATTEMPTS_THRESHOLD
        ) {
          lineItem.status = IndividualTransactionStatus.FAILED;
        }
      });
    });
  }

  private getParentTransactionStatusWithStartIndex(
    parent: TransactionStatusParent,
    index: number,
  ): ParentTransactionStatus {
    /**
     * If there's a single transaction:
     * - if it's successful, return TOTAL_SUCCESS
     * - if it's failed, return TOTAL_FAILURE
     * - if it's unknown, return UNKNOWN
     *
     * If there are multiple transactions:
     * - if the last transaction is SUCCESS, return PARTIAL_SUCCESS
     * - if the last transaction is FAILED, rerun the algorithm with the last transaction removed
     *   - if the result is TOTAL_SUCCESS, return PARTIAL_SUCCESS
     *   - if the result is PARTIAL_SUCCESS, return PARTIAL_SUCCESS
     *   - if the result is UNKNOWN, return UNKNOWN
     *   - if the result is TOTAL_FAILURE, return TOTAL_FAILURE
     * if the last transaction is UNKNOWN, rerun the algorithm with the last transaction removed
     *   - if the result is TOTAL_SUCCESS, return PARTIAL_SUCCESS
     *   - if the result is PARTIAL_SUCCESS, return PARTIAL_SUCCESS
     *   - if the result is UNKNOWN, return UNKNOWN
     *   - if the result is TOTAL_FAILURE, return UNKNOWN
     */

    const remainingLineItemsLength = parent.items.length - index;
    if (remainingLineItemsLength === 1) {
      const lineItem = parent.items[index];
      if (lineItem?.status === IndividualTransactionStatus.SUCCESS) {
        return ParentTransactionStatus.TOTAL_SUCCESS;
      } else if (lineItem?.status === IndividualTransactionStatus.FAILED) {
        return ParentTransactionStatus.TOTAL_FAILURE;
      } else {
        return ParentTransactionStatus.UNKNOWN;
      }
    } else {
      // remainingLineItemsLength > 1
      const tailStatus = this.getParentTransactionStatusWithStartIndex(parent, index + 1);
      const lineItem = parent.items[index];
      if (tailStatus === ParentTransactionStatus.TOTAL_FAILURE) {
        if (lineItem?.status === IndividualTransactionStatus.UNKNOWN) {
          return ParentTransactionStatus.UNKNOWN;
        } else if (lineItem?.status === IndividualTransactionStatus.FAILED) {
          return ParentTransactionStatus.TOTAL_FAILURE;
        } else {
          // lineItem?.status === IndividualTransactionStatus.SUCCESS
          return ParentTransactionStatus.PARTIAL_SUCCESS;
        }
      } else if (tailStatus === ParentTransactionStatus.TOTAL_SUCCESS) {
        return ParentTransactionStatus.PARTIAL_SUCCESS;
      } else {
        return tailStatus;
      }
    }
  }

  private getParentTransactionStatus(parent: TransactionStatusParent): ParentTransactionStatus {
    // TODO: how can we determine if a trx was accelerated vs cancelled?
    return this.getParentTransactionStatusWithStartIndex(parent, 0);
  }

  private getParentTransactionsWithUnknownStatus(): TransactionStatusParent[] {
    const pendingParents = Object.values(this.data).filter(parent => {
      const status = this.getParentTransactionStatus(parent);
      return status === ParentTransactionStatus.UNKNOWN;
    });
    return pendingParents;
  }

  private getLineItemToVerify(parent: TransactionStatusParent): TransactionStatusLineItem | null {
    const lineItem = parent.items.find(
      lineItem => lineItem.status === IndividualTransactionStatus.UNKNOWN,
    ); // get the first unknown line item
    return lineItem || null;
  }

  private async onVerificationComplete({
    txResponse,
    lineItem,
    status,
  }: {
    txResponse: FormattedTransaction | null;
    lineItem: TransactionStatusLineItem;
    status: IndividualTransactionStatus;
  }) {
    this.registerTransactionStatusVerificationResult({
      transaction: lineItem.transaction,
      status,
    });
    this.onChange(this.data);
    await this.onTransactionComplete({
      lineItem,
      txResponse, // TODO: cleanup all this leaky abstraction: https://linear.app/sound/issue/INFRA-956/go-over-the-todos-remove-leaky-abstraction-add-try-catch-add-tests
    });
  }

  private async verifyLineItem({
    lineItem,
    provider,
  }: {
    lineItem: TransactionStatusLineItem;
    provider: PublicClient;
  }) {
    this.registerTransactionStatusVerificationAttempt(lineItem.transaction);
    this.onChange(this.data);
    await this.onTransactionProcessing(lineItem.transaction);
    try {
      const txResponse = await getTransactionResponse({
        provider,
        hash: lineItem.transaction.hash,
        chainId: lineItem.transaction.chainId,
      });

      // This throws if the transaction reverted, in which case
      // we update its status in the catch block.
      const receipt = await provider.waitForTransactionReceipt({
        hash: lineItem.transaction.hash,
      });
      if (!receipt) throw new Error('Transaction receipt is undefined');

      const status = this.getTransactionStatus(receipt);
      await this.onVerificationComplete({
        txResponse,
        lineItem,
        status,
      });
    } catch (error) {
      await this.onVerificationComplete({
        lineItem,
        status: IndividualTransactionStatus.FAILED,
        txResponse: null,
      });

      throw new VerifyTransactionError({
        message: 'Error while verifying transaction',
        txHash: lineItem.transaction.hash,
        originalError: serializeError(error),
        chainId: lineItem.transaction.chainId,
      });
    }
  }

  public async onNewTransaction({
    txResponse,
    contractMethod,
    releaseId,
    provider,
    chainId,
  }: {
    txResponse: FormattedTransaction;
    contractMethod: ExtendedContractMethod;
    releaseId?: string;
    provider: PublicClient;
    chainId: number;
  }) {
    const lineItem = this.registerTransaction({
      hash: txResponse.hash,
      from: txResponse.from,
      nonce: txResponse.nonce,
      contractMethod,
      releaseId,
      chainId,
    });
    await this.verifyLineItem({
      lineItem,
      provider,
    });
  }

  public async verifyUnknownTransactions({
    now,
    provider,
  }: {
    now: Date;
    provider: ChainProviderContainerValue['provider'];
  }) {
    this.expireOldTransactions(now);
    this.onChange(this.data);
    const unknownParents = this.getParentTransactionsWithUnknownStatus();
    const lineItemsToVerify = unknownParents.map(parent => this.getLineItemToVerify(parent));

    for (const lineItem of lineItemsToVerify) {
      if (lineItem) {
        const chainProvider = provider(lineItem.transaction.chainId);

        await this.verifyLineItem({
          lineItem,
          provider: chainProvider,
        });
      }
    }
  }
}
